import React, { useCallback, useEffect, useMemo, useState } from "react";
import MrclText from '../../marcel/MrclText';
import { useTranslation } from "react-i18next";
import { displayValue, fmtEuro, fmtNum, fmtPct1Decimal } from "../../../utils/numberUtils";
import { FormatGroup, KpiData, RegionData, StoreData, TerritoryData } from "../../../types/commercial";
import { Card, ChevronContainer, FootText, HierarchyCell, Legend, MetricCell, MetricText, Table, TableWrapper, Toolbar, ToolbarActions } from "../components/commonComponents";
import { Breakpoint, Level } from "../Performance.model";
import useBreakPoint from "../../../utils/useBreakPoint";
import EmptyDataCard from "../components/EmptyDataCard";
import TableHeader from "../components/TableHeader";
import LoadingCard from "../components/LoadingCard";
import { ChevronIcon } from "../components/ChevronIcon";
import { COLORS } from "../../../utils/colors";
import TerritoryMobileList from "../TerritoryMobileList/MobileTerritoriesList";
import { formatTimeLabel } from "../../../utils/dateUtils";
import { useStores } from "../../../context/storesContext";
import { SortableHeader } from "../components/SortableHeader";
import { usePerformance } from "../context/PerformanceContext";

type LocalColumn = {
  key: string;
  header: React.ReactNode;
  isVisible: (breakpoint: Breakpoint) => boolean;
};

const emptyKpi = (): KpiData => ({
  caN1: 0,
  caN: 0,
  progCa: 0,
  progPrev: 0,
  clientsN: 0,
  clientsN1: 0,
  progClients: 0,
  quotePart: 0,
});

const COLUMN_WIDTHS: Record<string, string> = {
  caN1: "125px",
  caN: "125px",
  progCa: "120px",
  progPrev: "120px",
  clientsN: "120px",
  clientsN1: "125px",
  progClients: "130px",
  quotePart: "110px",
};


const srOnlyStyle: React.CSSProperties = {
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  borderWidth: 0,
};

// ─── KpiCells (shared across all row levels) ──────────────────────────────────


const hierarchyTitleStyle: React.CSSProperties = {
  fontSize: 16,
  color: "#1e293b",
  margin: 0,
};



interface KpiCellsProps {
  kpis?: KpiData;
  visibleColumns: LocalColumn[];
}


function PercentBadge({ value }: { value: string }) {
  const isNeg = value?.startsWith("-") && value !== "-";
  const isPos = value?.startsWith("+");
  return (
    <MetricText
      style={{
        color: isNeg ? COLORS.red : isPos ? COLORS.green : "#374151"
      }}
    >
      {value}
    </MetricText>
  );
}

const renderCell = (key: string, k: KpiData) => {
  switch (key) {
    case "caN1":
      return <PercentBadge value={displayValue(k.caN1, fmtEuro)} />;
    case "caN":
      return <PercentBadge value={displayValue(k.caN, fmtEuro)} />;
    case "progCa":
      return <PercentBadge value={displayValue(k.progCa, fmtPct1Decimal)} />;
    case "progPrev":
      return <PercentBadge value={displayValue(k.progPrev, fmtEuro)} />;
    case "clientsN":
      return <PercentBadge value={displayValue(k.clientsN, fmtNum)} />;
    case "clientsN1":
      return <PercentBadge value={displayValue(k.clientsN1, fmtNum)} />;
    case "progClients":
      return <PercentBadge value={displayValue(k.progClients, fmtPct1Decimal)} />;
    case "quotePart":
      return <PercentBadge value={displayValue(k.quotePart, (v) => `${v.toFixed(2)} %`)} />;
    default:
      return null;
  }
};

const KpiCells: React.FC<KpiCellsProps> = ({ kpis, visibleColumns }) => {
  const k = kpis || emptyKpi();

  return (
    <>
      {visibleColumns.map((col) => (
        <MetricCell
          key={col.key}
          style={{
            width: COLUMN_WIDTHS[col.key],
            minWidth: COLUMN_WIDTHS[col.key],
            maxWidth: COLUMN_WIDTHS[col.key],
            textAlign: "right",
            paddingRight: "16px",
          }}
        >
          {renderCell(col.key, k)}
        </MetricCell>
      ))}
    </>
  );
};

const getSortedStores = (
  stores: StoreData[],
  sortColumn: string | null,
  sortOrder: "none" | "asc" | "desc"
) => {
  const list = [...stores];
  if (!sortColumn || sortOrder === "none") {
    return list.sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr-FR"));
  }
  const kpiKey = sortColumn as keyof KpiData;
  return list.sort((a, b) => {
    const valA = Number(a.kpis?.[kpiKey] ?? 0);
    const valB = Number(b.kpis?.[kpiKey] ?? 0);
    return sortOrder === "asc" ? valB - valA : valA - valB;
  });
};

// Lignes agrégées (Groupe/DTI/Région) : les totaux ne doivent plus être affichés, quel que soit le magasin.
const EmptyKpiCells: React.FC<{ visibleColumns: LocalColumn[] }> = ({ visibleColumns }) => (
  <>
    {visibleColumns.map((col) => (
      <MetricCell
        key={col.key}
        style={{
          width: COLUMN_WIDTHS[col.key],
          minWidth: COLUMN_WIDTHS[col.key],
          maxWidth: COLUMN_WIDTHS[col.key],
          textAlign: "right",
          paddingRight: "16px",
        }}
      />
    ))}
  </>
);

// ─── StoreRow ─────────────────────────────────────────────────────────────────

interface StoreRowProps {
  store: StoreData;
  indent?: number;
  visibleColumns: LocalColumn[];
}

const StoreRow: React.FC<StoreRowProps> = ({ store, indent = 52, visibleColumns }) => {
  const { selectedStore } = useStores();
  const isCurrent = selectedStore?.storeKey === store?.storeKey;
  return (
  <tr
    style={{
      backgroundColor: "#FFFAF0",
    }}
  >
    <td style={{ padding: `10px 12px 10px ${indent}px`, borderLeft: "4px solid #8a5f00" }}>
      <div style={{ display: "flex", alignItems: "center" }}>
        <span style={{ display: "inline-block", width: 12, marginRight: 4, flexShrink: 0 }}></span>
        {/*<span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", backgroundColor: "#f97316", marginRight: 6, flexShrink: 0 }} /> */}
        <MrclText className="sr-only" style={srOnlyStyle}>Magasin</MrclText>
        <MrclText
            style={{
              ...hierarchyTitleStyle,
              textDecoration: isCurrent ? "underline" : "none",
            }}
          >
            {store?.name}
          </MrclText>
      </div>
    </td>
    <KpiCells kpis={store?.kpis} visibleColumns={visibleColumns} />
  </tr>);
};

// ─── RegionDataRow ───────────────────────────────────────────────────────────────────

interface RegionDataRowProps {
  region: RegionData;
  expanded: boolean;
  onToggle: () => void;
  levels: Level[];
  visibleColumns: LocalColumn[];
  sortOrder: "none" | "asc" | "desc";
  sortColumn: string | null;
}

const RegionDataRow: React.FC<RegionDataRowProps> = ({ region, expanded, onToggle, levels, visibleColumns, sortOrder, sortColumn }) => {
  // const totals = useMemo(() => sumKpis(region?.storesData || []), [region]);

  const sortedStores = useMemo(() => {
    return getSortedStores(region?.storesData || [], sortColumn, sortOrder);
  }, [region?.storesData, sortColumn, sortOrder]);

  return (
    <>
      <tr
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        tabIndex={0}
        style={{
          backgroundColor: "#Fefce8",
          cursor: "pointer",
        }}
      >
        <td style={{ padding: "10px 12px 10px 52px", borderLeft: `4px solid ${levels.find(({label}) => label === 'Magasin')?.color ?? ''}` }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <ChevronContainer style={{ color: levels.find(({label}) => label === 'Magasin')?.color ?? '', paddingRight: 4, flexShrink: 0 }}>
                <ChevronIcon open={expanded} />
            </ChevronContainer>
            <MrclText className="sr-only" style={srOnlyStyle}>Région</MrclText>
            <MrclText style={hierarchyTitleStyle}>{region?.name}</MrclText>
          </div>
        </td>
        {/* <KpiCells kpis={totals} visibleColumns={visibleColumns} /> */}
        <EmptyKpiCells visibleColumns={visibleColumns} />
      </tr>

      {expanded &&
        sortedStores.map((store, index) => (
          <StoreRow
            key={store.storeKey || String(index)}
            store={store}
            indent={72}
            visibleColumns={visibleColumns}
          />
        ))}
    </>
  );
};

// ─── TerritoryDataRow ───────────────────────────────────────────────────────────────────

interface TerritoryDataRowProps {
  dti: TerritoryData;
  expanded: boolean;
  expandedRegions: Set<string>;
  onToggle: () => void;
  onToggleRegion: (id: string) => void;
  levels : Level [];
  visibleColumns: LocalColumn[];
  showKpis?: boolean;
  sortOrder: "none" | "asc" | "desc";
  sortColumn: string | null;
}

const TerritoryDataRow: React.FC<TerritoryDataRowProps> = ({ dti, expanded, expandedRegions, onToggle, onToggleRegion, levels, visibleColumns, showKpis, sortOrder, sortColumn }) => {
  const hasRegions = !!dti?.regionsData;
  const sortedStores = useMemo(() => {
    return getSortedStores(dti?.storesData || [], sortColumn, sortOrder);
  }, [dti?.storesData, sortColumn, sortOrder]);

  return (
    <>
      <tr
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        tabIndex={0}
        style={{
          backgroundColor: COLORS.bg_green,
          cursor: "pointer",
        }}
      >
        <td style={{ padding: "10px 12px 10px 32px", borderLeft: `4px solid ${levels.find(({label}) => label === 'DTI')?.color ?? ''}` }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <ChevronContainer style={{ color: levels.find(({label}) => label === 'DTI')?.color ?? '', paddingRight: 4, flexShrink: 0 }}>
                <ChevronIcon open={expanded} />
            </ChevronContainer>
            <MrclText className="sr-only" style={srOnlyStyle}>DTI</MrclText>
            <MrclText style={hierarchyTitleStyle} bold>{dti?.name}</MrclText>
          </div>
        </td>
        {showKpis ? <KpiCells kpis={dti?.kpis} visibleColumns={visibleColumns} /> : <EmptyKpiCells visibleColumns={visibleColumns} />}
      </tr>

      {expanded && hasRegions && dti.regionsData && (
        <RegionDataRow
          key={dti.regionsData.id || "region-0"}
          levels={levels}
          region={dti.regionsData}
          expanded={expandedRegions.has(dti.regionsData.id || "region-0")}
          onToggle={() => onToggleRegion(dti.regionsData.id || "region-0")}
          visibleColumns={visibleColumns}
          sortOrder={sortOrder}
          sortColumn={sortColumn}
        />
      )}

      {expanded && !hasRegions &&
        sortedStores.map((store, index) => (
          <StoreRow
            key={store.storeKey || String(index)}
            store={store}
            indent={52}
            visibleColumns={visibleColumns}
          />
        ))}
    </>
  );
};

// ─── FormatGroupRow ─────────────────────────────────────────────────────────────────

interface FormatGroupRowProps {
  group: FormatGroup;
  expanded: boolean;
  expandedDtis: Set<string>;
  expandedRegions: Set<string>;
  levels: Level[];
  onToggleGroup: () => void;
  onToggleDti: (dtiId: string) => void;
  onToggleRegion: (regionId: string) => void;
  visibleColumns: LocalColumn[];
  showKpis?: boolean;
  sortOrder: "none" | "asc" | "desc";
  sortColumn: string | null;
}

const FormatGroupRow: React.FC<FormatGroupRowProps> = ({
  group,
  expanded,
  expandedDtis,
  expandedRegions,
  onToggleGroup,
  onToggleDti,
  onToggleRegion,
  levels,
  visibleColumns,
  showKpis,
  sortOrder,
  sortColumn,
}) => {

  return (
    <>
      <tr
        onClick={onToggleGroup}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggleGroup();
          }
        }}
        tabIndex={0}
        style={{
          backgroundColor: "#E8F8FF",
          cursor: "pointer",
        }}
      >
        <HierarchyCell style={{ borderLeft: `4px solid ${levels.find(({label}) => label === 'Groupe')?.color ?? ''}` }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <ChevronContainer style={{ color: levels.find(({label}) => label === 'Groupe')?.color ?? '', paddingRight: 4, flexShrink: 0 }}>
                <ChevronIcon open={expanded} />
            </ChevronContainer>
            {/* <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", backgroundColor: "#6366f1", marginRight: 6, flexShrink: 0 }} /> */}
            <MrclText className="sr-only" style={srOnlyStyle}>Groupe</MrclText>
            <MrclText style={hierarchyTitleStyle} bold>{group?.name?.toUpperCase()}</MrclText>
          </div>
        </HierarchyCell>
        {showKpis ? <KpiCells kpis={group?.kpis} visibleColumns={visibleColumns} /> : <EmptyKpiCells visibleColumns={visibleColumns} />}
      </tr>

      {expanded && group?.territoryData?.map((td, idx) => (
        <TerritoryDataRow
          levels={levels}
          key={td.id || `dti-group-${idx}`}
          dti={td}
          expanded={expandedDtis.has(td.id || `dti-group-${idx}`)}
          expandedRegions={expandedRegions}
          onToggle={() => onToggleDti(td.id || `dti-group-${idx}`)}
          onToggleRegion={onToggleRegion}
          visibleColumns={visibleColumns}
          showKpis={showKpis}
          sortOrder={sortOrder}
          sortColumn={sortColumn}
        />
      ))}
    </>
  );
};

// ─── HierarchyTable (root) ────────────────────────────────────────────────────

interface PerformanceByStoreTableProps {
  formatGroupData?: FormatGroup[] | null;
  isLoading?: boolean;
  range?: string;
  showKpis?: boolean;
  importedDateTime?: string;
}

interface TableExpansionState {
  groups: Set<string>;
  dtis: Set<string>;
  regions: Set<string>;
  isAllExpanded: boolean;
}

const PerformanceByStoreTable: React.FC<PerformanceByStoreTableProps> = ({formatGroupData, isLoading, range, showKpis, importedDateTime }) => {
  const { isCALive } = usePerformance();
  const [sort, setSort] = useState<{ column: string | null; order: "none" | "asc" | "desc" }>({
    column: null,
    order: "none",
  });

  const handleSortClick = (columnKey: string) => {
    setSort((prev) => {
      if (prev.column !== columnKey) {
        return { column: columnKey, order: "asc" };
      }
      if (prev.order === "none") {
        return { column: columnKey, order: "asc" };
      }
      if (prev.order === "asc") {
        return { column: columnKey, order: "desc" };
      }
      return { column: null, order: "none" };
    });
  };

  const [expansion, setExpansion] = useState<TableExpansionState>({
    groups: new Set(),
    dtis: new Set(),
    regions: new Set(),
    isAllExpanded: false,
  });
  const {t} = useTranslation();
  const breakPoint = useBreakPoint() as Breakpoint;
  const dataImportedDateTime = useMemo(() => {
    for (const group of formatGroupData ?? []) {
      if (group.kpis?.importedDateTime) return group.kpis.importedDateTime;
      for (const territory of group.territoryData ?? []) {
        if (territory.kpis?.importedDateTime) return territory.kpis.importedDateTime;
        if (territory.regionsData?.storesData) {
          for (const store of territory.regionsData.storesData) {
            if (store.kpis?.importedDateTime) return store.kpis.importedDateTime;
          }
        }
        for (const store of territory.storesData ?? []) {
          if (store.kpis?.importedDateTime) return store.kpis.importedDateTime;
        }
      }
    }

    return undefined;
  }, [formatGroupData]);
  const displayedImportedDateTime = importedDateTime ?? dataImportedDateTime;
  
  let rangeCol = range === "CUSTOM" ? "Manuel" :range;
  const METRIC_COLUMNS: LocalColumn[] = [
    {
      key: "caN1",
      header: (
        <div className="stable-header-cell">
          <SortableHeader
            label={`CA ${rangeCol}`}
            sortOrder={sort.column === "caN1" ? sort.order : "none"}
            onSortChange={() => handleSortClick("caN1")}
            isDesktop={breakPoint === "desktop"}
          />
        </div>
      ),
      isVisible: () => true,
    },
    {
      key: "caN",
      header: (
        <div className="stable-header-cell">
          <SortableHeader
            label="CA N"
            sortOrder={sort.column === "caN" ? sort.order : "none"}
            onSortChange={() => handleSortClick("caN")}
            isDesktop={breakPoint === "desktop"}
          />
        </div>
      ),
      isVisible: () => true,
    },
    {
      key: "progCa",
      header: (
        <div className="stable-header-cell">
          <SortableHeader
            label={isCALive ? "PROG CA" : `VAR ${rangeCol}`}
            sortOrder={sort.column === "progCa" ? sort.order : "none"}
            onSortChange={() => handleSortClick("progCa")}
            isDesktop={breakPoint === "desktop"}
          />
        </div>
      ),
      isVisible: () => true,
    },
    {
      key: "progPrev",
      header: (
        <div className="stable-header-cell">
          <SortableHeader
            label={isCALive ? "R-B" : "ECART"}
            sortOrder={sort.column === "progPrev" ? sort.order : "none"}
            onSortChange={() => handleSortClick("progPrev")}
            isDesktop={breakPoint === "desktop"}
          />
        </div>
      ),
      isVisible: () => true,
    },
    {
      key: "clientsN",
      header: (
        <div className="stable-header-cell">
          <SortableHeader
            label="Clients N"
            sortOrder={sort.column === "clientsN" ? sort.order : "none"}
            onSortChange={() => handleSortClick("clientsN")}
            isDesktop={breakPoint === "desktop"}
          />
        </div>
      ),
      isVisible: () => true,
    },
    {
      key: "clientsN1",
      header: (
        <div className="stable-header-cell">
          <SortableHeader
            label={`Clients ${rangeCol}`}
            sortOrder={sort.column === "clientsN1" ? sort.order : "none"}
            onSortChange={() => handleSortClick("clientsN1")}
            isDesktop={breakPoint === "desktop"}
          />
        </div>
      ),
      isVisible: () => false,
    },
    {
      key: "progClients",
      header: (
        <div className="stable-header-cell">
          <SortableHeader
            label={isCALive ? "PROG CLIENTS" : "VAR CLIENTS"}
            sortOrder={sort.column === "progClients" ? sort.order : "none"}
            onSortChange={() => handleSortClick("progClients")}
            isDesktop={breakPoint === "desktop"}
          />
        </div>
      ),
      isVisible: () => true,
    },
    {
      key: "quotePart",
      header: (
        <div className="stable-header-cell">
          QUOTE PART
        </div>
      ),
      isVisible:  () => false,
    },
  ];

const toggleGroup = (id: string) => {
  setExpansion((prev) => {
    const nextGroups = new Set(prev.groups);
    nextGroups.has(id) ? nextGroups.delete(id) : nextGroups.add(id);

    return {
      ...prev,
      groups: nextGroups,
      isAllExpanded: nextGroups.size > 0 ,
    };
  });
};

const toggleDti = (id: string) => {
  setExpansion((prev) => {
    const nextDtis = new Set(prev.dtis);
    nextDtis.has(id) ? nextDtis.delete(id) : nextDtis.add(id);

    return {
      ...prev,
      dtis: nextDtis,
      isAllExpanded: prev.groups.size > 0,
    };
  });
};

const toggleRegion = (id: string) => {
  setExpansion((prev) => {
    const nextRegions = new Set(prev.regions);
    nextRegions.has(id) ? nextRegions.delete(id) : nextRegions.add(id);

    return {
      ...prev,
      regions: nextRegions,
      isAllExpanded: prev.groups.size > 0 ,
    };
  });
};

const expandAll = useCallback(() => {
  if (formatGroupData?.length) {
    setExpansion({
      groups: new Set(["0"]),
      dtis: new Set(
        formatGroupData[0]?.territoryData?.map((td, idx) => td.id || `dti-group-${idx}`) ?? []
      ),
      regions: new Set(
        formatGroupData[0]?.territoryData
          ?.filter(td => td.regionsData)
          .map(td => td.regionsData.id || "region-0") ?? []
      ),
      isAllExpanded: true,
    });
  }
}, [formatGroupData]);

const collapseAll = useCallback(() => {
  setExpansion({
    groups: new Set(),
    dtis: new Set(),
    regions: new Set(),
    isAllExpanded: false,
  });
}, []);

  useEffect(() => {
    if (formatGroupData?.length) {
      expandAll();
    } else {
      collapseAll();
    }
  }, [formatGroupData, expandAll, collapseAll]);

  const isMobileOrTablet = breakPoint !== "desktop";

  if (showKpis && isMobileOrTablet) {
    return <TerritoryMobileList formatGroupData={formatGroupData} isLoading={isLoading} />;
  }

  const visibleColumns = METRIC_COLUMNS.filter((column) => column.isVisible(breakPoint));
  const levels : Level[] = [
      { color: "#173EB4", label: t('commercial.groupe') },
      { color: COLORS.green, label: t('commercial.dti') },
      ...(!formatGroupData?.[0]?.name?.includes('hyper') ? [{ color: "#f59e0b", label: t('commercial.region') }] : []),
      { color: "#8A5F00", label: t('commercial.magasin') },
  ]

  if (isLoading) {
    return <LoadingCard />;
  }

  if (!formatGroupData?.length) {
    return <EmptyDataCard/>
  }
const onExpand = () => {
  setExpansion((prev) => {
    return prev.isAllExpanded
      ? {
          groups: new Set(),
          dtis: new Set(),
          regions: new Set(),
          isAllExpanded: false,
        }
      : {
          groups: new Set(["0"]),
          dtis: new Set(
            formatGroupData[0]?.territoryData?.map((td, idx) => td.id || `dti-group-${idx}`) ?? []
          ),
          regions: new Set(
            formatGroupData[0]?.territoryData
              ?.filter(td => td.regionsData)
              .map(td => td.regionsData.id || "region-0") ?? []
          ),
          isAllExpanded: true,
        };
  });
};

  return (
      <Card>
        <style>{`
          .stable-header-cell {
            display: flex !important;
            align-items: center !important;
            justify-content: flex-end !important;
            width: 100% !important;
            box-sizing: border-box !important;
          }

          /* Interactive header button wrapper */
          .stable-header-cell > * {
            display: inline-flex !important;
            align-items: center !important;
            justify-content: flex-end !important;
            gap: 6px !important; /* Fixed gap between label and icon */
            background: none !important;
            border: none !important;
            margin: 0 !important;
            padding: 0 !important;
            cursor: pointer;
            position: relative !important;
          }

          /* Target the icon container directly inside SortableHeader to enforce a constant footprint */
          .stable-header-cell > * button,
          .stable-header-cell > * .c-icon-button,
          .stable-header-cell > * .c-button,
          .stable-header-cell [data-testid*="icon-button-chevron"] {
            position: relative !important; /* Remove absolute positioning */
            top: auto !important;
            right: auto !important;
            transform: none !important;
            margin: 0 !important;
            padding: 0 !important;
            background: none !important;
            border: none !important;
            width: 16px !important;  /* Reserve fixed width for all icon states */
            height: 16px !important; /* Reserve fixed height */
            min-width: 16px !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            flex-shrink: 0 !important;
          }

          /* Ensure th and td align consistently */
          th:not(:first-child),
          td:not(:first-child) {
            text-align: right !important;
            padding-right: 16px !important;
            padding-left: 16px !important;
          }
        `}</style>
        {/* <TableToolbar levels={levels} expandAll={expandAll} collapseAll={collapseAll} /> */}
        <TableWrapper>
          <Table style={{ tableLayout: "fixed", width: "100%" }}>
            <colgroup>
              <col style={{ width: "280px" }} />
              {visibleColumns.map((col) => (
                <col key={col.key} style={{ width: COLUMN_WIDTHS[col.key] }} />
              ))}
            </colgroup>
            <TableHeader visibleColumns={visibleColumns} title={t('commercial.hierarchie')}>
              <mrcl-icon-button
                variant="transparent"
                tone="neutral"
                style={{ background: 'none', color: '#005CA9', border: 'none', padding: 0, margin: 0 }}
                icon={expansion.isAllExpanded ? "chevron-double-down" : "chevron-double-up"}
                onClick={onExpand}
               />                          
            </TableHeader>
            <tbody>
              {formatGroupData.map((group, idx) => (
                <FormatGroupRow
                  key={String(idx)}
                  levels={levels}
                  group={group}
                  expanded={expansion.groups.has(String(idx))}
                  expandedDtis={expansion.dtis}
                  expandedRegions={expansion.regions}
                  onToggleGroup={() => toggleGroup(String(idx))}
                  onToggleDti={toggleDti}
                  onToggleRegion={toggleRegion}
                  visibleColumns={visibleColumns}
                  showKpis={showKpis}
                  sortOrder={sort.order}
                  sortColumn={sort.column}
                />
              ))}
            </tbody>
          </Table>
        </TableWrapper>
        {!!displayedImportedDateTime &&
          <Toolbar>
            <Legend>
              <FootText>
                {`${t('commercial.data-updated')} ${formatTimeLabel(displayedImportedDateTime)}`}
              </FootText>
            </Legend>

            <ToolbarActions>
              <FootText>
                {`${t('commercial.total-lignes')} ${formatGroupData.length}`}
              </FootText>
            </ToolbarActions>
          </Toolbar>}
    </Card>
  );
};

export default PerformanceByStoreTable;
